import { defineComponent as _defineComponent } from 'vue';
import { library } from "@fortawesome/fontawesome-svg-core";
import { faChevronCircleRight, faMinusSquare } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { useDebounce } from "@vueuse/core";
import { storeToRefs } from "pinia";
import { computed, inject, onBeforeUnmount, ref, toRefs, watch, watchEffect, } from "vue";
import { DatatypesMapperModel } from "@/components/Datatypes/model";
import { ConnectionAcceptable, terminalFactory, } from "@/components/Workflow/Editor/modules/terminals";
import { useWorkflowStores } from "@/composables/workflowStores";
import { getConnectionId } from "@/stores/workflowConnectionStore";
import { useRelativePosition } from "./composables/relativePosition";
import { useTerminal } from "./composables/useTerminal";
export default /*#__PURE__*/ _defineComponent({
    __name: 'NodeInput',
    props: {
        input: {
            type: Object,
            required: true,
        },
        stepId: {
            type: Number,
            required: true,
        },
        datatypesMapper: {
            type: DatatypesMapperModel,
            required: true,
        },
        stepPosition: {
            type: Object,
            required: true,
        },
        rootOffset: {
            type: Object,
            required: true,
        },
        scale: {
            type: Number,
            required: true,
        },
        scroll: {
            type: Object,
            required: true,
        },
        parentNode: {
            type: HTMLElement,
            default: null,
        },
        readonly: {
            type: Boolean,
            default: false,
        },
        blank: {
            type: Boolean,
            default: false,
        },
    },
    setup(__props) {
        const props = __props;
        library.add(faChevronCircleRight, faMinusSquare);
        onBeforeUnmount(() => {
            stateStore.deleteInputTerminalPosition(props.stepId, props.input.name);
        });
        const { stepId, input, datatypesMapper } = toRefs(props);
        const id = computed(() => `node-${props.stepId}-input-${props.input.name}`);
        const { terminal, isMappedOver: isMultiple } = useTerminal(stepId, input, datatypesMapper);
        const dropTarget = ref(null);
        const position = useRelativePosition(dropTarget, computed(() => props.parentNode));
        const stores = useWorkflowStores();
        const { connectionStore, stateStore } = stores;
        const hasTerminals = ref(false);
        watchEffect(() => {
            hasTerminals.value = connectionStore.getOutputTerminalsForInputTerminal(id.value).length > 0;
        });
        const connections = computed(() => {
            return connectionStore.getConnectionsForTerminal(id.value);
        });
        const invalidConnectionReasons = computed(() => connections.value
            .map((connection) => connectionStore.invalidConnections[getConnectionId(connection)])
            .filter((reason) => reason));
        const { draggingTerminal } = storeToRefs(stateStore);
        const canAccept = computed(() => {
            if (draggingTerminal.value) {
                return terminal.value.canAccept(draggingTerminal.value);
            }
            else {
                const firstReason = invalidConnectionReasons.value[0];
                if (firstReason) {
                    return new ConnectionAcceptable(false, firstReason);
                }
            }
            return null;
        });
        const acceptsInput = computed(() => {
            var _a;
            if ((_a = canAccept.value) === null || _a === void 0 ? void 0 : _a.canAccept) {
                return true;
            }
            else {
                return false;
            }
        });
        const endX = computed(() => { var _a, _b; return position.value.offsetLeft + props.stepPosition.left + ((_b = (_a = dropTarget.value) === null || _a === void 0 ? void 0 : _a.offsetWidth) !== null && _b !== void 0 ? _b : 2) / 2; });
        const endY = computed(() => { var _a, _b; return position.value.offsetTop + props.stepPosition.top + ((_b = (_a = dropTarget.value) === null || _a === void 0 ? void 0 : _a.offsetHeight) !== null && _b !== void 0 ? _b : 2) / 2; });
        watch([endX, endY], ([x, y]) => {
            stateStore.setInputTerminalPosition(props.stepId, props.input.name, { endX: x, endY: y });
        });
        const isDragging = inject("isDragging");
        const reason = computed(() => { var _a, _b; return (_b = (_a = canAccept.value) === null || _a === void 0 ? void 0 : _a.reason) !== null && _b !== void 0 ? _b : undefined; });
        const label = computed(() => props.input.label || props.input.name);
        const hasConnections = computed(() => connections.value.length > 0);
        const rowClass = computed(() => {
            var _a;
            const classes = ["form-row", "dataRow", "input-data-row"];
            if (!props.blank && ((_a = props.input) === null || _a === void 0 ? void 0 : _a.valid) === false) {
                classes.push("form-row-error");
            }
            return classes;
        });
        const showTooltip = ref(false);
        function dragEnter(event) {
            if (reason.value) {
                showTooltip.value = true;
            }
            event.preventDefault();
        }
        function dragLeave(_event) {
            showTooltip.value = false;
        }
        function onRemove() {
            const connections = connectionStore.getConnectionsForTerminal(id.value);
            connections.forEach((connection) => terminal.value.disconnect(connection));
        }
        function onDrop(event) {
            if (!event.dataTransfer) {
                return;
            }
            const stepOut = JSON.parse(event.dataTransfer.getData("text/plain"));
            const droppedTerminal = terminalFactory(stepOut.stepId, stepOut.output, props.datatypesMapper, stores);
            showTooltip.value = false;
            if (terminal.value.canAccept(droppedTerminal).canAccept) {
                terminal.value.connect(droppedTerminal);
            }
        }
        const draggedOver = ref(false);
        const draggedOverDebounced = useDebounce(draggedOver, 50);
        function nodeDragOver() {
            draggedOver.value = true && Boolean(draggingTerminal.value);
        }
        function nodeDragOut() {
            draggedOver.value = false;
        }
        watch(() => draggingTerminal.value, () => {
            if (!draggingTerminal.value) {
                draggedOver.value = false;
            }
        });
        return { __sfc: true, props, stepId, input, datatypesMapper, id, terminal, isMultiple, dropTarget, position, stores, connectionStore, stateStore, hasTerminals, connections, invalidConnectionReasons, draggingTerminal, canAccept, acceptsInput, endX, endY, isDragging, reason, label, hasConnections, rowClass, showTooltip, dragEnter, dragLeave, onRemove, onDrop, draggedOver, draggedOverDebounced, nodeDragOver, nodeDragOut, FontAwesomeIcon };
    }
});
